import { AnyObject } from '@xbcb/shared-types';
import { WorkOrderMilestoneName } from '@xbcb/work-order-types';
import { InvoiceStatus, ChargeCode } from '@xbcb/finance-types';
import type { UsIorContinuousBondRequest } from '@xbcb/api-gateway-client';

const getMilestoneNames = (record: AnyObject) => {
  return (
    record?.milestones.map(
      ({ name }: { name: WorkOrderMilestoneName }) => name,
    ) || []
  );
};

// checks if continuous bond was acquired successfully or not
// conditions to check:
// 1) a continuous bond acquisition request using bond bot failed and no subsequent success (ideally on a retry)
// 2) The charge bond query didn't succeed
export const continuousBondFailedFromMilestones = (
  record: AnyObject,
): boolean => {
  const milestoneNames = getMilestoneNames(record);
  return (
    milestoneNames.includes(
      WorkOrderMilestoneName.US_IOR_CONTINUOUS_BOND_REQUEST_ACQUISITION_FAILED,
    ) &&
    !milestoneNames.includes(
      WorkOrderMilestoneName.US_IOR_CONTINUOUS_BOND_REQUEST_ACQUISITION_SUCCEEDED,
    ) &&
    !milestoneNames.includes(
      WorkOrderMilestoneName.US_IOR_CONTINUOUS_BOND_REQUEST_CHARGE_BOND_SUCCEEDED,
    )
  );
};

// checks if continuous bond was requested
export const continuousBondRequestedFromMilestones = (
  record: UsIorContinuousBondRequest,
): boolean => {
  const milestoneNames = getMilestoneNames(record);
  return milestoneNames.includes(
    WorkOrderMilestoneName.US_IOR_CONTINUOUS_BOND_REQUEST_ACQUISITION_REQUESTED,
  );
};

export const continuousBondInvoiceExists = (
  record: UsIorContinuousBondRequest,
): boolean => {
  const invoiceExists = record?.group?.invoices?.some(
    ({ lines, status }) =>
      lines &&
      lines.some((line) => line.type === ChargeCode.CONTINUOUS_BOND) &&
      ![InvoiceStatus.PENDING_VOID, InvoiceStatus.VOID].includes(
        status as InvoiceStatus,
      ),
  );
  return Boolean(invoiceExists);
};

interface RecordWithContinuousBonds {
  usIor?: {
    continuousBonds?: {
      present?: {
        terminationDate?: string | null;
      } | null;
    } | null;
  } | null;
}

/**
 * Returns true if the IOR already has an open-ended active continuous bond that
 * makes a new acquisition redundant.
 *
 * The gate blocks only when the `present` bond has NO `terminationDate` — i.e. it is
 * active with no scheduled end, so acquiring another would create a duplicate. Once a
 * `terminationDate` is issued (even a *future* one), the bond is winding down and the
 * operator must be able to acquire the renewal early: bond sureties often require lead
 * time, and waiting until after the termination date risks a coverage gap. A future
 * bond is intentionally NOT considered here — a renewal still needs its own bond
 * request work order even if the bond was acquired offline.
 *
 * Used to prevent redundant acquisition requests without blocking legitimate renewals.
 * @param {RecordWithContinuousBonds} record - The bond request record containing usIor with continuousBonds data.
 * @return {boolean} Whether the IOR already has an open-ended active continuous bond.
 */
export const hasActiveContinuousBond = (
  record: RecordWithContinuousBonds | null | undefined,
): boolean => {
  const presentBond = record?.usIor?.continuousBonds?.present;
  return Boolean(presentBond && !presentBond.terminationDate);
};
